import { Clock, Material, type Mesh, type Object3D, type Scene } from 'three';
import { type OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { publish } from '@pkg/components/Sections/Profile/pubSub';
import { glUpdateTopic } from '@pkg/components/Sections/Profile/pubSub/constants';

import Camera from './globals/Camera';
import { glSettings } from './globals/Constants';
import Controls from './globals/Controls';
import Renderer from './globals/Renderer';

const { USE_ORBIT_CONTROLS, DEBUG_DRAW_COUNT } = glSettings;

export default class Base {
    isRunning = false;
    requestFrame?: number;
    elapsed = 0;
    camera: Camera;
    renderer: Renderer;
    canvas: HTMLCanvasElement;
    drawCallCount?: number;
    now: number;
    controls?: OrbitControls;
    width: number;
    height: number;
    delta: number;
    clock: Clock;

    constructor(parentElement, isFullScreen) {
        this.clock = new Clock();
        this.now = Date.now();

        this.delta = 0;

        this.width = isFullScreen ? window.innerWidth : parentElement.clientWidth;
        this.height = isFullScreen ? window.innerHeight : parentElement.clientHeight;

        this.camera = Camera.getInstance();
        this.renderer = Renderer.getInstance(parentElement, isFullScreen);

        this.canvas = this.renderer.domElement;

        if (USE_ORBIT_CONTROLS) {
            this.initControls();
        }

        if (DEBUG_DRAW_COUNT) {
            this.drawCallCount = 0;
            this.renderer.info.autoReset = false;
        }

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    start() {
        this.clock.start();
        this.isRunning = true;
        this.run();
    }

    initControls() {
        this.controls = Controls.getInstance(this.camera, this.renderer);
        this.controls.enabled = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.update();
    }

    handleVisibilityChange = () => {
        document.visibilityState === 'visible' ? this.start() : this.pause();
    };

    destroy(renderer: Renderer, scene?: Scene) {
        if (this.requestFrame) {
            cancelAnimationFrame(this.requestFrame);
            this.requestFrame = undefined;
        }
        this.rendererDispose(renderer);
        if (scene) this.sceneDispose(scene);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    cleanMaterial = (material: Material | Material[]) => {
        if (material instanceof Material) {
            material.dispose();
        }
        for (const value of Object.values(material)) {
            if (value && typeof value === 'object' && 'minFilter' in value) {
                value.dispose();
            }
        }
    };

    rendererDispose(renderer: Renderer) {
        renderer.dispose();
        renderer.renderLists.dispose();
        renderer.destroy();
    }

    sceneDispose(scene: Scene) {
        if (scene) {
            scene.traverse((object: Mesh | Object3D) => {
                setTimeout(() => {
                    if (object.type === 'Mesh') {
                        const mesh = object as Mesh;
                        mesh.geometry.dispose();
                        if (mesh.material) {
                            this.cleanMaterial(mesh.material);
                        }
                    }
                    scene.remove(object);
                }, 1);
            });
        }
    }

    pause() {
        this.clock.stop();
        if (this.requestFrame) {
            cancelAnimationFrame(this.requestFrame);
            this.requestFrame = undefined;
        }
        this.isRunning = false;
    }

    update(elapsed: number, delta: number) {
        // TODO THUNDER: This is not perf. Needs to be improve
        publish(glUpdateTopic, { elapsed, delta });
    }

    run() {
        if (!this.isRunning) {
            this.now = Date.now();
        }

        this.delta = this.clock.getDelta();
        this.elapsed = this.clock.getElapsedTime();

        // console.log(this.delta * 100);

        if (this.controls) {
            this.controls.update();
        }

        this.update(this.elapsed, this.delta);

        if (DEBUG_DRAW_COUNT) {
            const { calls } = this.renderer.info.render;

            this.drawCallCount = calls;
            this.renderer.info.reset();
        }

        if (this.isRunning) {
            this.requestFrame = requestAnimationFrame(this.run.bind(this));
        }
    }
}
